import { useQuery } from '@apollo/client';
import React, { useContext, useMemo, useState } from 'react';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { IStandardRutinWrapperProps } from '../../../../_StandardRutin/StandardRutin';
import {
  BokforderWrapperContext,
  IBokforderWrapperContext,
} from '../Contexts/BokforderWrapperContext';
import bokforderWrapperQuery from '../Queries/BokforderWrapperQuery.gql';
import {
  BokforderWrapperQuery,
  BokforderWrapperQueryVariables,
} from '../Queries/_QueryTypes/BokforderWrapperQuery';
import sysoptionQuery from '../Queries/EKBokforderSysoptionQuery.gql';
import {
  EKBokforderSysoptionQuery,
  EKBokforderSysoptionQueryVariables,
} from '../Queries/_QueryTypes/EKBokforderSysoptionQuery';
import {
  EK_BOKFORDER_ATTESTANT_EDIT_KONTERING,
  EK_BOKFORDER_UTANORDNING_ACTIVE,
  EK_BOKFORDER_UTANORDNING_DUBBEL_ATTEST,
  EK_USE_IMPORT_FROM_CLIPBOARD,
} from '../Const/BokforderSysoptionConsts';
import { notNullOrUndefined } from '../../../../../../Utils/utils';
import usersQuery from '../../../ObjektVyer/Bokforder/Queries/BokforderKodbehorigheter/BokforderKodbehorigheter.gql';
import {
  UserBokforderKodbehorigheterQuery,
  UserBokforderKodbehorigheterQueryVariables,
  UserBokforderKodbehorigheterQuery_userBokforderKodbehorigheter,
} from '../../../ObjektVyer/Bokforder/Queries/BokforderKodbehorigheter/_QueryTypes/UserBokforderKodbehorigheterQuery';
import {
  GAS_CONTROLLER_CODE,
  GAS_EKONOMIASSISTENT_CODE,
  GAS_EKONOM_CODE,
} from '../../../../../../Const/RolesConsts';
import FranvaroContextManager from '../../../../SystemAdministration/ObjektVyer/User/Context/FranvaroContext/FranvaroContextManager';

interface IProps extends IStandardRutinWrapperProps {}

export enum CreateDialogOpenEnum {
  CLOSED = 'CLOSED',
  OPENFROMDETAIL = 'OPENFROMDETAIL',
  OPENFROMLIST = 'OPENFROMLIST',
}

export enum AdditionalAttestDialogOpenEnum {
  CLOSED = 'CLOSED',
  ATTESTERA = 'ATTESTERA',
  ATERTA = 'ATERTA',
}

const BokforderProviderWrapper: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { userID, activeDatabase } = useContext(ActiveDatabaseContext);

  const [createOptionDialogOpen, setCreateOptionDialogOpen] =
    useState<CreateDialogOpenEnum>(CreateDialogOpenEnum.CLOSED);
  const [reservAttestDialogOpen, setAdditionalAttestDialogOpen] =
    useState<AdditionalAttestDialogOpenEnum>(
      AdditionalAttestDialogOpenEnum.CLOSED
    );

  const { data: currentUserData, loading: currentUserDataLoading } = useQuery<
    BokforderWrapperQuery,
    BokforderWrapperQueryVariables
  >(bokforderWrapperQuery, {
    fetchPolicy: 'cache-and-network',
    variables: {
      dbname: activeDatabase || '',
      signUserID: userID || '',
      gasroller: [
        GAS_EKONOM_CODE,
        GAS_EKONOMIASSISTENT_CODE,
        GAS_CONTROLLER_CODE,
      ],
    },
    skip: !activeDatabase || !userID,
  });

  const sysOptionsQuery = useQuery<
    EKBokforderSysoptionQuery,
    EKBokforderSysoptionQueryVariables
  >(sysoptionQuery, {
    variables: {
      dbName: activeDatabase ?? '',
      optionNames: [
        { value: EK_USE_IMPORT_FROM_CLIPBOARD },
        { value: EK_BOKFORDER_UTANORDNING_ACTIVE },
        { value: EK_BOKFORDER_ATTESTANT_EDIT_KONTERING },
        { value: EK_BOKFORDER_UTANORDNING_DUBBEL_ATTEST },
      ],
      routine: 15006,
    },
    skip: !activeDatabase,
  });

  const { data, loading } = useQuery<
    UserBokforderKodbehorigheterQuery,
    UserBokforderKodbehorigheterQueryVariables
  >(usersQuery, {
    variables: { dbname: activeDatabase ?? '' },
  });

  const allBFOUsers: UserBokforderKodbehorigheterQuery_userBokforderKodbehorigheter[] =
    useMemo(() => {
      return !loading && data?.userBokforderKodbehorigheter
        ? data?.userBokforderKodbehorigheter.filter(notNullOrUndefined)
        : [];
    }, [data?.userBokforderKodbehorigheter, loading]);

  const currentUser = useMemo(() => {
    return currentUserData?.db?.SYSUser_All?.[0];
  }, [currentUserData]);

  const allBokfar = useMemo(() => {
    return currentUserData?.db?.SYSBokfar_All ?? [];
  }, [currentUserData]);

  const isEconomyPersonnel = useMemo(() => {
    return currentUserData?.me?.[0]?.hargasroll ?? false;
  }, [currentUserData?.me]);

  const sysOptions = sysOptionsQuery.data?.db?.SYSOptions_All;

  const value: IBokforderWrapperContext = useMemo(
    () => ({
      allBokfar,
      allBFOUsers,
      allBFOUsersLoading: loading,
      createOptionDialogOpen,
      currentUser,
      currentUserDataLoading,
      isEconomyPersonnel,
      reservAttestDialogOpen,
      setCreateOptionDialogOpen,
      setAdditionalAttestDialogOpen,
      sysOptions,
    }),
    [
      allBokfar,
      allBFOUsers,
      loading,
      createOptionDialogOpen,
      currentUser,
      currentUserDataLoading,
      isEconomyPersonnel,
      reservAttestDialogOpen,
      sysOptions,
    ]
  );

  return (
    <BokforderWrapperContext.Provider value={value}>
      <FranvaroContextManager routineNumber={15006}>
        {props.children}
      </FranvaroContextManager>
    </BokforderWrapperContext.Provider>
  );
};

export default BokforderProviderWrapper;
